import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip,
  TextField, Button, MenuItem, Divider, Stack, ToggleButton,
  ToggleButtonGroup, Autocomplete, LinearProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { ArrowBack, Home, Groups, Add, Delete, Person, AutoAwesome, Search, WarningAmber, Edit } from '@mui/icons-material';
import axios from 'axios';
import TimetableNav from './TimetableNav';

const API = process.env.REACT_APP_API_URL;

// A full teaching week ≈ number of lesson slots; flag teachers pushing past a sane cap.
const WEEKLY_CAP = 35;

const emptyForm = {
  kind: 'core', employeeNo: '', areaCode: '', classCodes: [],
  cohortCode: '', optionBlock: '', lessonsPerWeek: 4, doubleLessons: 0,
};

const TeacherAllocations = () => {
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [years, setYears] = useState([]);
  const [year, setYear]   = useState('');
  const [term, setTerm]   = useState(1);

  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classes, setClasses]   = useState([]);
  const [stages, setStages]     = useState([]);

  const [allocations, setAllocations] = useState([]);
  const [load, setLoad] = useState([]);

  const [form, setForm]       = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [subjectAssignments, setSubjectAssignments] = useState([]); // selected teacher's Subject-Teacher rows
  const [pulling, setPulling] = useState(false);

  // List search / filter / sort
  const [q, setQ] = useState('');
  const [kindFilter, setKindFilter] = useState('all');
  const [sortBy, setSortBy] = useState('teacher');

  // Row editor (kind / class / cohort / block …)
  const [editing, setEditing]   = useState(null);   // the allocation being edited
  const [editForm, setEditForm] = useState(emptyForm);
  const [editErr, setEditErr]   = useState('');

  // Reference data (once)
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/academics/years`, { headers }),
      axios.get(`${API}/api/employees`, { headers, params: { department: 'Teaching' } }),
      axios.get(`${API}/api/academics/learning-areas`, { headers }),
      axios.get(`${API}/api/academics/classes`, { headers }),
      axios.get(`${API}/api/stages`, { headers }),
      axios.get(`${API}/api/academics/current-period`, { headers }).catch(() => ({ data: {} })),
    ]).then(([y, t, s, c, st, cp]) => {
      setYears(y.data);
      // Default to the current period: the active year and the term in session.
      const active = y.data.find(yr => yr.isActive) || y.data[0];
      if (cp.data?.year) setYear(cp.data.year);
      else if (active)   setYear(active.year);
      if (cp.data?.term) setTerm(cp.data.term);
      setTeachers(t.data);
      setSubjects(s.data);
      setClasses(c.data);
      setStages(st.data);
    }).catch(err => setError(err.response?.data?.error || 'Failed to load reference data'));
  }, []); // eslint-disable-line

  const teacherName = useCallback((no) => {
    const t = teachers.find(x => x.employeeNo === no);
    return t ? `${t.firstName} ${t.lastName}` : no;
  }, [teachers]);
  const subjectName = useCallback((code) => subjects.find(s => s.areaCode === code)?.areaName || code, [subjects]);

  const loadData = useCallback(async () => {
    if (!year || !term) return;
    setLoading(true); setError('');
    try {
      const [a, l] = await Promise.all([
        axios.get(`${API}/api/timetable/allocations`, { headers, params: { academicYear: year, term } }),
        axios.get(`${API}/api/timetable/allocations/load`, { headers, params: { academicYear: year, term } }),
      ]);
      setAllocations(a.data);
      setLoad(l.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load allocations');
    } finally { setLoading(false); }
  }, [year, term]); // eslint-disable-line

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 2500);
    return () => clearTimeout(t);
  }, [success]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // When a teacher is picked, pull their Subject-Teacher (stage-subject) assignments
  // so we can auto-fill allocations from them instead of re-typing subject + classes.
  useEffect(() => {
    if (!form.employeeNo || !year) { setSubjectAssignments([]); return; }
    axios.get(`${API}/api/academics/stage-subject-teachers`, {
      headers, params: { academicYear: year, employeeNo: form.employeeNo },
    }).then(r => setSubjectAssignments(r.data || [])).catch(() => setSubjectAssignments([]));
  }, [form.employeeNo, year]); // eslint-disable-line

  // Auto-pick a subject assignment into the form: core lesson, every class in the
  // stage, defaulting to 4 lessons/week (still editable before Add).
  const applyAssignment = (a) => {
    const classCodes = classes.filter(c => c.stageCode === a.stageCode).map(c => c.classCode);
    setForm(f => ({
      ...f, kind: 'core', areaCode: a.areaCode, classCodes,
      lessonsPerWeek: parseInt(f.lessonsPerWeek, 10) || 4,
    }));
  };

  // Already-allocated (subject|stage) pairs for this teacher, so a chip that's
  // been fully added can show as done.
  const isAllocated = (a) => {
    const stageClassSet = new Set(classes.filter(c => c.stageCode === a.stageCode).map(c => c.classCode));
    return allocations.some(al => al.employeeNo === form.employeeNo && al.areaCode === a.areaCode &&
      (al.classCodes || []).some(cc => stageClassSet.has(cc)));
  };

  // Fill GAPS from the Subject-Teacher assignments — additive only, never touches
  // what's already there. For each assignment it adds a core allocation for just
  // the streams in that stage that have NO teacher yet for the subject (by any
  // teacher). Streams you've already allocated — or reassigned while cleaning up —
  // are left exactly as they are; fully-covered subjects are skipped entirely.
  const pullFromSubjectTeachers = async () => {
    setError('');
    setPulling(true);
    try {
      const { data: assigns } = await axios.get(`${API}/api/academics/stage-subject-teachers`,
        { headers, params: { academicYear: year } });
      if (!assigns || assigns.length === 0) {
        setError('No Subject Teacher assignments found for this year — assign them first.');
        return;
      }
      const toCreate = [];
      const claimed = new Set();   // areaCode|classCode we're already adding this run
      const noClassStages = new Set();   // stages that have assignments but no classes yet
      assigns.forEach(a => {
        const stageClasses = classes.filter(c => c.stageCode === a.stageCode).map(c => c.classCode);
        if (stageClasses.length === 0) { noClassStages.add(a.stageCode); return; }   // stage has no classes yet
        // Streams in this stage that ALREADY have a teacher for this subject (any teacher).
        const covered = new Set();
        allocations.forEach(al => {
          if (al.kind === 'elective' || al.areaCode !== a.areaCode) return;
          (al.classCodes || []).forEach(cc => { if (stageClasses.includes(cc)) covered.add(cc); });
        });
        const missing = stageClasses.filter(cc => !covered.has(cc) && !claimed.has(`${a.areaCode}|${cc}`));
        if (missing.length === 0) return;   // subject already covered across this stage → nothing to add
        missing.forEach(cc => claimed.add(`${a.areaCode}|${cc}`));
        toCreate.push({
          academicYear: year, term, kind: 'core',
          employeeNo: a.employeeNo, areaCode: a.areaCode, classCodes: missing,
          lessonsPerWeek: 4, doubleLessons: 0,
        });
      });
      // Name any stages that have Subject-Teacher assignments but no classes yet —
      // those are silently un-pullable until their classes/streams are created.
      const stageName = (code) => stages.find(s => s.code === code)?.name || code;
      const noClassMsg = noClassStages.size
        ? ` ${noClassStages.size} stage${noClassStages.size !== 1 ? 's' : ''} skipped — no classes created yet: ${[...noClassStages].map(stageName).join(', ')}. Add their classes under Structure › Classes, then pull again.`
        : '';
      if (toCreate.length === 0) {
        const base = noClassStages.size
          ? 'No allocations added.'
          : 'All Subject Teacher assignments are already allocated for this term.';
        if (noClassMsg) setError(base + noClassMsg); else setSuccess(base);
        return;
      }
      const results = await Promise.allSettled(
        toCreate.map(p => axios.post(`${API}/api/timetable/allocations`, p, { headers })));
      const added   = results.filter(r => r.status === 'fulfilled').length;
      const skipped = results.length - added;
      setSuccess(`Added ${added} missing allocation${added !== 1 ? 's' : ''} from Subject Teachers${skipped ? ` · ${skipped} skipped (conflicts)` : ''}. Existing allocations were left untouched.`);
      // Surface un-pullable stages in the persistent error alert (the success
      // banner auto-dismisses) so the missing-classes cause stays actionable.
      if (noClassMsg) setError(`Added ${added}, but${noClassMsg}`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to pull from Subject Teachers');
    } finally { setPulling(false); }
  };

  const addAllocation = async () => {
    setError('');
    if (!form.employeeNo || !form.areaCode) { setError('Teacher and subject are required'); return; }
    if (form.kind === 'core' && form.classCodes.length === 0) { setError('Pick at least one class for a core lesson'); return; }
    if (form.kind === 'elective' && !form.cohortCode) { setError('Pick a cohort for an elective'); return; }
    // Block an exact duplicate before it reaches the server.
    if (allocations.some(a => dupKey(a) === dupKey(form))) { setError('That allocation already exists.'); return; }
    setSaving(true);
    try {
      const payload = {
        academicYear: year, term, kind: form.kind,
        employeeNo: form.employeeNo, areaCode: form.areaCode,
        classCodes: form.kind === 'core' ? form.classCodes : [],
        cohortCode: form.kind === 'elective' ? form.cohortCode : '',
        optionBlock: form.kind === 'elective' ? form.optionBlock : '',
        lessonsPerWeek: parseInt(form.lessonsPerWeek, 10) || 4,
        doubleLessons: parseInt(form.doubleLessons, 10) || 0,
      };
      await axios.post(`${API}/api/timetable/allocations`, payload, { headers });
      setForm(f => ({ ...emptyForm, kind: f.kind, employeeNo: f.employeeNo })); // keep teacher for fast multi-entry
      setSuccess('Allocation added');
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add allocation');
    } finally { setSaving(false); }
  };

  const updateLessons = async (id, field, value) => {
    try {
      await axios.put(`${API}/api/timetable/allocations/${id}`, { [field]: parseInt(value, 10) || 0 }, { headers });
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Update failed'); }
  };

  const openEdit = (a) => {
    setEditErr('');
    setEditForm({
      kind: a.kind || 'core',
      employeeNo: a.employeeNo,
      areaCode: a.areaCode,
      classCodes: a.classCodes || [],
      cohortCode: a.cohortCode || '',
      optionBlock: a.optionBlock || '',
      lessonsPerWeek: a.lessonsPerWeek ?? 1,
      doubleLessons: a.doubleLessons ?? 0,
    });
    setEditing(a);
  };

  const setEdit = (field, value) => setEditForm(f => ({ ...f, [field]: value }));

  const saveEdit = async () => {
    setEditErr('');
    if (!editForm.employeeNo || !editForm.areaCode) { setEditErr('Teacher and subject are required'); return; }
    if (editForm.kind === 'core' && editForm.classCodes.length === 0) { setEditErr('Pick at least one class for a core lesson'); return; }
    if (editForm.kind === 'elective' && !editForm.cohortCode) { setEditErr('Pick a cohort for an elective'); return; }
    setSaving(true);
    try {
      const payload = {
        kind: editForm.kind,
        employeeNo: editForm.employeeNo,
        areaCode: editForm.areaCode,
        classCodes: editForm.kind === 'core' ? editForm.classCodes : [],
        cohortCode: editForm.kind === 'elective' ? editForm.cohortCode : '',
        optionBlock: editForm.kind === 'elective' ? editForm.optionBlock : '',
        lessonsPerWeek: parseInt(editForm.lessonsPerWeek, 10) || 1,
        doubleLessons: parseInt(editForm.doubleLessons, 10) || 0,
      };
      await axios.put(`${API}/api/timetable/allocations/${editing._id}`, payload, { headers });
      setEditing(null);
      setSuccess('Allocation updated');
      loadData();
    } catch (err) {
      setEditErr(err.response?.data?.error || 'Failed to update allocation');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/api/timetable/allocations/${id}`, { headers });
      setSuccess('Allocation removed');
      loadData();
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  const maxLoad = useMemo(() => Math.max(WEEKLY_CAP, ...load.map(l => l.lessons)), [load]);

  // Duplicate detection: same teacher+subject+kind+scope appearing more than once.
  const scopeOf = (a) => a.kind === 'elective'
    ? `${a.cohortCode}|${a.optionBlock || ''}`
    : (a.classCodes || []).slice().sort().join(',');
  const dupKey = (a) => [a.kind, a.employeeNo, a.areaCode, scopeOf(a)].join('¦');
  const dupCounts = useMemo(() => {
    const m = {};
    allocations.forEach(a => { const k = dupKey(a); m[k] = (m[k] || 0) + 1; });
    return m;
  }, [allocations]); // eslint-disable-line react-hooks/exhaustive-deps
  const dupTotal = useMemo(() => Object.values(dupCounts).filter(n => n > 1).reduce((s, n) => s + (n - 1), 0), [dupCounts]);

  // Filtered + sorted rows for the table.
  const view = useMemo(() => {
    let rows = allocations;
    if (kindFilter !== 'all') rows = rows.filter(a => a.kind === kindFilter);
    const s = q.trim().toLowerCase();
    if (s) {
      rows = rows.filter(a =>
        teacherName(a.employeeNo).toLowerCase().includes(s) ||
        subjectName(a.areaCode).toLowerCase().includes(s) ||
        (a.areaCode || '').toLowerCase().includes(s) ||
        (a.kind === 'elective' ? (a.cohortCode || '') : (a.classCodes || []).join(',')).toLowerCase().includes(s));
    }
    const clsLabel = (a) => a.kind === 'elective' ? (a.cohortCode || '') : (a.classCodes || []).join(',');
    const cmp = {
      teacher: (a, b) => teacherName(a.employeeNo).localeCompare(teacherName(b.employeeNo)),
      subject: (a, b) => subjectName(a.areaCode).localeCompare(subjectName(b.areaCode)),
      kind:    (a, b) => (a.kind || '').localeCompare(b.kind || ''),
      class:   (a, b) => clsLabel(a).localeCompare(clsLabel(b)),
      lessons: (a, b) => (b.lessonsPerWeek || 0) - (a.lessonsPerWeek || 0),
    }[sortBy] || (() => 0);
    return [...rows].sort(cmp);
  }, [allocations, kindFilter, q, sortBy, teacherName, subjectName]);

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, px: { xs: 1, sm: 0 } }}>
      <TimetableNav />
      <Card elevation={3}>
        <CardContent>
          {/* Header */}
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Groups color="primary" />
              <Box>
                <Typography variant="h5" color="primary">Teacher Allocations</Typography>
                <Typography variant="body2" color="text.secondary">
                  Assign teachers to classes &amp; subjects — the demand behind the timetable
                </Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <IconButton onClick={() => navigate('/academics')} title="Back to Academics"><ArrowBack /></IconButton>
              <IconButton onClick={() => navigate('/dashboard')} title="Home"><Home /></IconButton>
            </Box>
          </Box>

          {/* Year / Term */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
            <TextField select label="Academic Year" value={year} size="small" sx={{ minWidth: 180 }}
              onChange={e => setYear(e.target.value)}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}{y.isActive ? ' (active)' : ''}</MenuItem>)}
            </TextField>
            <TextField select label="Term" value={term} size="small" sx={{ minWidth: 120 }}
              onChange={e => setTerm(parseInt(e.target.value, 10))}>
              {[1, 2, 3].map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
            </TextField>
            <Tooltip title="Create allocations for every Subject Teacher assignment this year (core, all classes in the stage, 4 lessons/wk — editable)">
              <Button variant="outlined" startIcon={pulling ? <CircularProgress size={14} /> : <AutoAwesome />}
                onClick={pullFromSubjectTeachers} disabled={pulling}
                sx={{ ml: { sm: 'auto' }, whiteSpace: 'nowrap' }}>
                {pulling ? 'Pulling…' : 'Pull from Subject Teachers'}
              </Button>
            </Tooltip>
          </Stack>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {/* ── Add allocation form ── */}
          <Card variant="outlined" sx={{ mb: 3, bgcolor: '#fafafa' }}>
            <CardContent>
              <ToggleButtonGroup size="small" exclusive value={form.kind}
                onChange={(_, v) => v && set('kind', v)} sx={{ mb: 2 }}>
                <ToggleButton value="core">Core (per stream)</ToggleButton>
                <ToggleButton value="elective">Elective (option block)</ToggleButton>
              </ToggleButtonGroup>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap" useFlexGap>
                <TextField select label="Teacher *" value={form.employeeNo} size="small" sx={{ minWidth: 220 }}
                  onChange={e => set('employeeNo', e.target.value)}>
                  {teachers.map(t => (
                    <MenuItem key={t.employeeNo} value={t.employeeNo}>{t.firstName} {t.lastName} · {t.employeeNo}</MenuItem>
                  ))}
                </TextField>

                <TextField select label="Subject *" value={form.areaCode} size="small" sx={{ minWidth: 180 }}
                  onChange={e => set('areaCode', e.target.value)}>
                  {subjects.map(s => <MenuItem key={s.areaCode} value={s.areaCode}>{s.areaName} ({s.areaCode})</MenuItem>)}
                </TextField>

                {form.kind === 'core' ? (
                  <Autocomplete multiple size="small" sx={{ minWidth: 260 }}
                    options={classes.map(c => c.classCode)}
                    value={form.classCodes}
                    onChange={(_, v) => set('classCodes', v)}
                    renderInput={(params) => <TextField {...params} label="Class(es) *" placeholder="stream(s)" />}
                  />
                ) : (
                  <>
                    <TextField select label="Cohort *" value={form.cohortCode} size="small" sx={{ minWidth: 160 }}
                      onChange={e => set('cohortCode', e.target.value)}>
                      {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.name} ({s.code})</MenuItem>)}
                    </TextField>
                    <TextField label="Option Block" value={form.optionBlock} size="small" sx={{ minWidth: 120 }}
                      placeholder="e.g. A" onChange={e => set('optionBlock', e.target.value.toUpperCase())} />
                  </>
                )}

                <TextField label="Lessons/wk" value={form.lessonsPerWeek} type="number" size="small" sx={{ width: 110 }}
                  inputProps={{ min: 1 }} onChange={e => set('lessonsPerWeek', e.target.value)} />
                <TextField label="Doubles" value={form.doubleLessons} type="number" size="small" sx={{ width: 100 }}
                  inputProps={{ min: 0 }} onChange={e => set('doubleLessons', e.target.value)} />

                <Button variant="contained" startIcon={<Add />} onClick={addAllocation} disabled={saving}>
                  Add
                </Button>
              </Stack>

              {/* Auto-pick from the teacher's Subject-Teacher assignments */}
              {form.employeeNo && subjectAssignments.length > 0 && (
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5} mb={0.75}>
                    <AutoAwesome fontSize="inherit" /> From this teacher's subject assignments — click to auto-fill (all classes in the stage, 4 lessons/wk, editable):
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={0.75}>
                    {subjectAssignments.map(a => {
                      const done = isAllocated(a);
                      return (
                        <Chip key={a._id} size="small" clickable variant={done ? 'filled' : 'outlined'}
                          color={done ? 'success' : 'primary'}
                          label={`${a.areaName || a.areaCode} · ${stages.find(s => s.code === a.stageCode)?.name || a.stageCode}`}
                          onClick={() => applyAssignment(a)} />
                      );
                    })}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* ── Per-teacher load meter ── */}
          {load.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight={700} mb={1} display="flex" alignItems="center" gap={0.5}>
                <Person fontSize="small" /> Weekly Load per Teacher
              </Typography>
              <Box sx={{ mb: 3, maxHeight: 220, overflowY: 'auto' }}>
                {load.map(l => {
                  const over = l.lessons > WEEKLY_CAP;
                  return (
                    <Box key={l.employeeNo} display="flex" alignItems="center" gap={1.5} mb={0.8}>
                      <Typography variant="body2" sx={{ minWidth: 190 }} noWrap>{teacherName(l.employeeNo)}</Typography>
                      <Box flex={1}>
                        <LinearProgress variant="determinate" value={Math.min(100, (l.lessons / maxLoad) * 100)}
                          color={over ? 'error' : 'primary'} sx={{ height: 8, borderRadius: 4 }} />
                      </Box>
                      <Tooltip title={`${l.subjects} subject allocation(s)`}>
                        <Chip size="small" label={`${l.lessons} / wk`} color={over ? 'error' : 'default'}
                          variant={over ? 'filled' : 'outlined'} sx={{ minWidth: 66 }} />
                      </Tooltip>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          <Divider sx={{ mb: 2 }} />

          {/* ── Search / filter / sort ── */}
          {allocations.length > 0 && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
              <TextField size="small" label="Search" value={q} onChange={e => setQ(e.target.value)}
                placeholder="teacher, subject, class" sx={{ minWidth: 210 }}
                InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 0.5, opacity: 0.6 }} /> }} />
              <TextField select size="small" label="Kind" value={kindFilter} onChange={e => setKindFilter(e.target.value)} sx={{ minWidth: 130 }}>
                <MenuItem value="all">All kinds</MenuItem>
                <MenuItem value="core">Core</MenuItem>
                <MenuItem value="elective">Elective</MenuItem>
              </TextField>
              <TextField select size="small" label="Sort by" value={sortBy} onChange={e => setSortBy(e.target.value)} sx={{ minWidth: 150 }}>
                <MenuItem value="teacher">Teacher</MenuItem>
                <MenuItem value="subject">Subject</MenuItem>
                <MenuItem value="kind">Kind</MenuItem>
                <MenuItem value="class">Class / Cohort</MenuItem>
                <MenuItem value="lessons">Lessons/wk</MenuItem>
              </TextField>
              <Chip size="small" variant="outlined" label={`${view.length} of ${allocations.length}`} />
            </Stack>
          )}
          {dupTotal > 0 && (
            <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
              {dupTotal} duplicate allocation{dupTotal !== 1 ? 's' : ''} found (same teacher, subject &amp; class). Rows flagged “Duplicate” below — delete the extras.
            </Alert>
          )}

          {/* ── Allocations table ── */}
          {loading ? (
            <Box textAlign="center" py={4}><CircularProgress /></Box>
          ) : allocations.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No allocations yet for {year || '—'} Term {term}. Add teaching assignments above.
            </Typography>
          ) : view.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>No allocations match your search/filter.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Teacher</strong></TableCell>
                    <TableCell><strong>Subject</strong></TableCell>
                    <TableCell><strong>Kind</strong></TableCell>
                    <TableCell><strong>Class / Cohort</strong></TableCell>
                    <TableCell align="center"><strong>Lessons/wk</strong></TableCell>
                    <TableCell align="center"><strong>Doubles</strong></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {view.map(a => {
                    const isDup = dupCounts[dupKey(a)] > 1;
                    return (
                    <TableRow key={a._id} hover sx={isDup ? { bgcolor: '#fff8e1' } : undefined}>
                      <TableCell>{teacherName(a.employeeNo)}</TableCell>
                      <TableCell>{subjectName(a.areaCode)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={a.kind}
                          color={a.kind === 'elective' ? 'secondary' : 'primary'} variant="outlined"
                          sx={{ textTransform: 'capitalize' }} />
                        {isDup && <Chip size="small" color="warning" label="Duplicate" sx={{ ml: 0.5 }} />}
                      </TableCell>
                      <TableCell>
                        {a.kind === 'elective'
                          ? <>{a.cohortCode}{a.optionBlock ? <Chip size="small" label={`Block ${a.optionBlock}`} sx={{ ml: 0.5 }} /> : null}</>
                          : (a.classCodes || []).join(', ')}
                      </TableCell>
                      <TableCell align="center">
                        <TextField value={a.lessonsPerWeek} type="number" size="small" variant="standard"
                          inputProps={{ min: 1, style: { textAlign: 'center', width: 44 } }}
                          onChange={e => setAllocations(al => al.map(x => x._id === a._id ? { ...x, lessonsPerWeek: e.target.value } : x))}
                          onBlur={e => updateLessons(a._id, 'lessonsPerWeek', e.target.value)} />
                      </TableCell>
                      <TableCell align="center">
                        <TextField value={a.doubleLessons} type="number" size="small" variant="standard"
                          inputProps={{ min: 0, style: { textAlign: 'center', width: 44 } }}
                          onChange={e => setAllocations(al => al.map(x => x._id === a._id ? { ...x, doubleLessons: e.target.value } : x))}
                          onBlur={e => updateLessons(a._id, 'doubleLessons', e.target.value)} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <IconButton size="small" color="primary" onClick={() => openEdit(a)} title="Edit allocation">
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => remove(a._id)} title="Remove allocation">
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Edit allocation dialog ── */}
      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Allocation</DialogTitle>
        <DialogContent dividers>
          {editErr && <Alert severity="error" sx={{ mb: 2 }}>{editErr}</Alert>}
          <ToggleButtonGroup size="small" exclusive value={editForm.kind}
            onChange={(_, v) => v && setEdit('kind', v)} sx={{ mb: 2 }}>
            <ToggleButton value="core">Core (per stream)</ToggleButton>
            <ToggleButton value="elective">Elective (option block)</ToggleButton>
          </ToggleButtonGroup>

          <Stack spacing={2}>
            <TextField select label="Teacher *" value={editForm.employeeNo} size="small" fullWidth
              onChange={e => setEdit('employeeNo', e.target.value)}>
              {teachers.map(t => (
                <MenuItem key={t.employeeNo} value={t.employeeNo}>{t.firstName} {t.lastName} · {t.employeeNo}</MenuItem>
              ))}
            </TextField>

            <TextField select label="Subject *" value={editForm.areaCode} size="small" fullWidth
              onChange={e => setEdit('areaCode', e.target.value)}>
              {subjects.map(s => <MenuItem key={s.areaCode} value={s.areaCode}>{s.areaName} ({s.areaCode})</MenuItem>)}
            </TextField>

            {editForm.kind === 'core' ? (
              <Autocomplete multiple size="small"
                options={classes.map(c => c.classCode)}
                value={editForm.classCodes}
                onChange={(_, v) => setEdit('classCodes', v)}
                renderInput={(params) => <TextField {...params} label="Class(es) *" placeholder="stream(s)" />}
              />
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField select label="Cohort *" value={editForm.cohortCode} size="small" fullWidth
                  onChange={e => setEdit('cohortCode', e.target.value)}>
                  {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.name} ({s.code})</MenuItem>)}
                </TextField>
                <TextField label="Option Block" value={editForm.optionBlock} size="small" sx={{ minWidth: 140 }}
                  placeholder="e.g. A" onChange={e => setEdit('optionBlock', e.target.value.toUpperCase())} />
              </Stack>
            )}

            <Stack direction="row" spacing={2}>
              <TextField label="Lessons/wk" value={editForm.lessonsPerWeek} type="number" size="small" sx={{ width: 130 }}
                inputProps={{ min: 1 }} onChange={e => setEdit('lessonsPerWeek', e.target.value)} />
              <TextField label="Doubles" value={editForm.doubleLessons} type="number" size="small" sx={{ width: 130 }}
                inputProps={{ min: 0 }} onChange={e => setEdit('doubleLessons', e.target.value)} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={saveEdit} disabled={saving}>Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherAllocations;
